/**
 * BusinessDetail — Life › Business
 * The landing page for Lev's business ventures. Only Levanta is live right now;
 * Evolvex and The Eilati are placeholders ("coming soon"). Levanta opens the
 * live Levanta roadmap board, shared bidirectionally with /roadmap.
 */
import { motion } from 'framer-motion';
import { ChevronRight, Lock, TrendingUp, Rocket, Palmtree } from 'lucide-react';

const BUSINESSES = [
  {
    id: 'levanta',
    name: 'Levanta',
    tagline: 'The Life OS you\'re building',
    blurb: 'Roadmap, milestones and future ideas — synced live with levanta.life/roadmap.',
    icon: TrendingUp,
    status: 'live',
  },
  {
    id: 'evolvex',
    name: 'Evolvex',
    tagline: 'Fitness / performance concept',
    blurb: 'Not yet tracked in NEXA. When you\'re ready, tell NEXA to open it up.',
    icon: Rocket,
    status: 'soon',
  },
  {
    id: 'the-eilati',
    name: 'The Eilati',
    tagline: 'Eilat brand · lifestyle',
    blurb: 'Not yet tracked in NEXA. When you\'re ready, tell NEXA to open it up.',
    icon: Palmtree,
    status: 'soon',
  },
];

export const BusinessDetail = ({ onOpenLevanta }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="flex flex-col gap-4"
      data-testid="nexa-business-view"
    >
      <div>
        <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.3em] text-sky-400/80 uppercase">
          <span className="w-4 h-px bg-sky-400/50" /> Business
        </div>
        <p className="text-white/45 text-sm mt-2">
          Three ventures. Only one is live in NEXA — the other two are on standby.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {BUSINESSES.map((b) => {
          const Icon = b.icon;
          const live = b.status === 'live';
          return (
            <button
              key={b.id}
              disabled={!live}
              onClick={() => live && b.id === 'levanta' && onOpenLevanta()}
              data-testid={`nexa-business-${b.id}`}
              className={`relative w-full text-left rounded-2xl border p-5 flex items-start gap-4 transition-all bg-[#0A0A0A] ${
                live
                  ? 'border-white/10 hover:border-sky-400/50 hover:shadow-[0_0_24px_rgba(56,189,248,0.18)] active:scale-[0.99]'
                  : 'border-white/[0.06] opacity-60 cursor-default'
              }`}
            >
              <span className={`absolute top-2 left-2 w-2.5 h-2.5 border-t border-l ${live ? 'border-sky-400/50' : 'border-white/15'}`} />
              <span className={`absolute top-2 right-2 w-2.5 h-2.5 border-t border-r ${live ? 'border-sky-400/50' : 'border-white/15'}`} />
              <span className={`absolute bottom-2 left-2 w-2.5 h-2.5 border-b border-l ${live ? 'border-sky-400/50' : 'border-white/15'}`} />
              <span className={`absolute bottom-2 right-2 w-2.5 h-2.5 border-b border-r ${live ? 'border-sky-400/50' : 'border-white/15'}`} />

              <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${live ? 'bg-sky-400/10 text-sky-400' : 'bg-white/[0.04] text-white/30'}`}>
                {live ? <Icon className="w-5 h-5" strokeWidth={1.5} /> : <Lock className="w-5 h-5" strokeWidth={1.5} />}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[13px] font-mono tracking-[0.18em] uppercase ${live ? 'text-white/90' : 'text-white/50'}`}>
                    {b.name}
                  </span>
                  {live ? (
                    <span className="text-[9px] font-mono tracking-[0.22em] uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
                      Live
                    </span>
                  ) : (
                    <span className="text-[9px] font-mono tracking-[0.22em] uppercase px-1.5 py-0.5 rounded bg-white/[0.05] text-white/40 ring-1 ring-white/10">
                      Coming soon
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/40 mt-1">{b.tagline}</div>
                <p className="text-[12px] text-white/55 mt-2 leading-relaxed">{b.blurb}</p>
              </div>

              {live && (
                <ChevronRight className="w-4 h-4 text-white/30 mt-1 shrink-0" strokeWidth={2} />
              )}
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-[10px] font-mono tracking-[0.2em] uppercase text-white/25 text-center px-6">
        Edits inside Levanta sync live with /roadmap
      </p>
    </motion.div>
  );
};

export default BusinessDetail;
